import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCollage, getRelatedBooks } from '../../data';
import './CollagePage.css';
import Breadcrumbs from '../common/Breadcrumbs';
import BookShelf from '../common/BookShelf';
import NotFound from '../common/NotFound';
import { IconContext } from 'react-icons';
import { MdFullscreen } from 'react-icons/md';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import { getImagePaths, getBestImagePath, shouldUseThumbnail } from '../../utils/imageUtils';
import useDocumentMeta from '../../hooks/useDocumentMeta';

const CollagePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const collage = getCollage(id || '');
    const relatedBooks = getRelatedBooks(collage?.bookIds || []);

    const allImages = collage
        ? [collage.images.main, ...(collage.images.additional || [])]
        : [];

    // Full-size sources, for the hero image and the lightbox only.
    const imagePaths = allImages.map(img =>
        getBestImagePath(getImagePaths(img), shouldUseThumbnail('detail'))
    );

    // The thumbnail strip used to reuse `imagePaths`, so a five-image
    // collage downloaded roughly 8MB of full-resolution WebP to render
    // five 80px squares. These are the ~40KB versions.
    const thumbPaths = allImages.map(img =>
        getBestImagePath(getImagePaths(img), shouldUseThumbnail('grid'))
    );

    useDocumentMeta({
        title: collage?.title,
        description: collage?.description,
        image: thumbPaths[0],
        type: 'article'
    });

    if (!collage) {
        return <NotFound thing="collage" backTo={{ label: 'collages', path: '/collages' }} />;
    }

    return (
        <div className="collage-page">
            <Breadcrumbs
                items={[
                    { label: 'collages', path: '/collages' },
                    { label: collage.title }
                ]}
            />

            {/* One tree for every viewport. This used to be two ~90-line
                branches on `isMobile` whose only real difference was
                whether <aside> or <main> came first in the DOM — which is
                a CSS ordering concern, not a JSX one. */}
            <div className="collage-layout">
                <header className="collage-header">
                    <h1>{collage.title}</h1>
                    <span className="collage-date">{collage.date}</span>
                </header>

                <div className="collage-content">
                    <main className="collage-main">
                        <div className="collage-image-container">
                            <IconContext.Provider value={{ className: 'fullscreen-icon' }}>
                                <button
                                    type="button"
                                    className="fullscreen-toggle"
                                    onClick={() => setIsLightboxOpen(true)}
                                    aria-label={`View ${collage.title} full screen`}
                                >
                                    <MdFullscreen />
                                </button>
                            </IconContext.Provider>
                            <img
                                src={imagePaths[currentImageIndex]}
                                alt={
                                    allImages.length > 1
                                        ? `${collage.title}, image ${currentImageIndex + 1} of ${allImages.length}`
                                        : collage.title
                                }
                                className="collage-image"
                                decoding="async"
                            />

                            {thumbPaths.length > 1 && (
                                <div className="collage-thumbnails">
                                    {thumbPaths.map((path, index) => (
                                        <button
                                            type="button"
                                            key={allImages[index]}
                                            className={`thumbnail-button ${currentImageIndex === index ? 'active' : ''}`}
                                            onClick={() => setCurrentImageIndex(index)}
                                            aria-label={`Show image ${index + 1} of ${allImages.length}`}
                                            aria-current={currentImageIndex === index}
                                        >
                                            <img
                                                src={path}
                                                alt=""
                                                className="thumbnail-image"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </main>

                    <aside className="sidebar">
                        {collage.description && (
                            <div className="collage-description-section">
                                <h2>Description</h2>
                                <div className="collage-description">
                                    <p>{collage.description}</p>
                                </div>
                            </div>
                        )}

                        {relatedBooks.length > 0 && (
                            <div className="books-section">
                                <h2>Related Books</h2>
                                {/* The accordion this replaced hid every cover
                                    behind a click, to show the same description
                                    the book page already carries. */}
                                <BookShelf books={relatedBooks} size="small" />
                            </div>
                        )}
                    </aside>
                </div>
            </div>

            <Lightbox
                open={isLightboxOpen}
                close={() => setIsLightboxOpen(false)}
                slides={imagePaths.map((path, i) => ({
                    src: path,
                    alt: `${collage.title}, image ${i + 1} of ${allImages.length}`
                }))}
                plugins={[Zoom, Counter]}
                index={currentImageIndex}
                on={{
                    view: ({ index }) => setCurrentImageIndex(index)
                }}
                zoom={{
                    wheelZoomDistanceFactor: 100,
                    pinchZoomDistanceFactor: 100,
                    scrollToZoom: true
                }}
            />
        </div>
    );
};

export default CollagePage;
